import { useEffect, useState } from "react";
import { useTheme } from "@mui/material/styles";
import {
  Box,
  Typography,
  Paper,
  Grid,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Chip,
  Button,
  CircularProgress,
  Alert,
  Avatar,
  Breadcrumbs,
  Link,
  LinearProgress,
  Divider,
} from "@mui/material";
import { useNavigate, useParams } from "react-router-dom";
import {
  fetchStudentCourseProgress,
  fetchStudentAttendance,
  fetchChecklistsForCourse,
} from "../../api/backend";

import NavigateNextIcon from "@mui/icons-material/NavigateNext";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import CancelIcon from "@mui/icons-material/Cancel";

const DS = {
  surface: "var(--color-surface-2)",
  border: "rgba(0,0,0,0.08)",
};

function StatusChip({ status }) {
  const map = {
    Present: { bgcolor: "var(--color-success-bg)", color: "#15803d" },
    Tardy: { bgcolor: "var(--color-warn-bg)", color: "var(--color-warn)" },
    "No-Show": { bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)" },
    "Call-Out": { bgcolor: "#ede9fe", color: "#6d28d9" },
    Excused: { bgcolor: "var(--color-primary-10)", color: "var(--color-primary)" },
  };
  const s = map[status] || { bgcolor: "var(--color-surface-2)", color: "var(--color-ink-2)" };
  return (
    <Chip
      label={status || "—"}
      size="small"
      sx={{ bgcolor: s.bgcolor, color: s.color, fontSize: "0.7rem", fontWeight: 700 }}
    />
  );
}

function SectionHeader({ title }) {
  const theme = useTheme();
  return (
    <Typography variant="subtitle1" fontWeight={800} color={theme.palette.primary.main} sx={{ mb: 1.5, fontFamily: "var(--font-display)" }}>
      {title}
    </Typography>
  );
}

export default function InstructorStudentDashboard() {
  const theme = useTheme();
  const tPrimary = theme.palette.primary.main;
  const { courseId, studentId } = useParams();
  const navigate = useNavigate();

  const [progress, setProgress] = useState(null);
  const [attendance, setAttendance] = useState([]);
  const [checklists, setChecklists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!courseId || !studentId) return;
    setLoading(true);
    Promise.all([
      fetchStudentCourseProgress(courseId, studentId),
      fetchStudentAttendance(courseId, studentId),
      fetchChecklistsForCourse(courseId),
    ])
      .then(([p, a, c]) => {
        setProgress(p || {});
        setAttendance(a || []);
        setChecklists(c || []);
      })
      .catch((err) => {
        setError("We couldn't load this right now. Please try again.");
        console.error(err);
      })
      .finally(() => setLoading(false));
  }, [courseId, studentId]);

  const studentName = progress?.studentName || `Student ${studentId}`;

  // Summarise attendance
  const presentCount = attendance.filter((r) => r.status === "Present").length;
  const tardyCount = attendance.filter((r) => r.status === "Tardy").length;
  const noShowCount = attendance.filter((r) => r.status === "No-Show").length;
  const attendanceRate =
    attendance.length > 0
      ? Math.round(((presentCount + tardyCount) / attendance.length) * 100)
      : null;

  // Checklist summary
  const checklistStats = checklists.map((cl) => {
    const studentEval = progress?.checklistEvaluations?.[cl.id];
    return {
      ...cl,
      peerDone: studentEval?.peerComplete || false,
      instructorDone: studentEval?.instructorComplete || false,
      grade: studentEval?.gradePercent ?? null,
    };
  });

  // Scenario summary
  const scenarios = progress?.scenarios || [];

  return (
    <Box sx={{ maxWidth: 1000, mx: "auto" }}>

      {/* Breadcrumb */}
      <Breadcrumbs separator={<NavigateNextIcon fontSize="small" />} sx={{ mb: 2.5 }}>
        <Link
          underline="hover" color="inherit" sx={{ cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => navigate("/instructor/courses")}
        >
          Assigned Classes
        </Link>
        <Link
          underline="hover" color="inherit" sx={{ cursor: "pointer", fontSize: "0.85rem" }}
          onClick={() => navigate(`/course-management/${courseId}`)}
        >
          Course Hub
        </Link>
        <Typography color="text.primary" sx={{ fontSize: "0.85rem", fontWeight: 600 }}>
          {studentName}
        </Typography>
      </Breadcrumbs>

      {/* Student header card */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5, mb: 3, borderRadius: "12px",
          border: `1px solid ${DS.border}`,
          display: "flex", alignItems: "center", gap: 2,
          bgcolor: tPrimary,
        }}
      >
        <Avatar sx={{ width: 56, height: 56, bgcolor: "rgba(255,255,255,0.15)", fontSize: "1.3rem", fontWeight: 800 }}>
          {studentName[0]}
        </Avatar>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h6" fontWeight={800} color="#fff" sx={{ fontFamily: "var(--font-display)" }}>{studentName}</Typography>
          {progress?.agencyId && (
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)" }}>
              Agency ID: {progress.agencyId}
            </Typography>
          )}
        </Box>
        {progress?.rank !== undefined && (
          <Box sx={{ textAlign: "center", pr: 1 }}>
            <Typography variant="h4" fontWeight={900} color="#fff" sx={{ fontFamily: "var(--font-display)" }}>{progress.rank}</Typography>
            <Typography variant="caption" sx={{ color: "rgba(255,255,255,0.7)", fontSize: "0.7rem" }}>
              RANKED OF {progress.totalStudents ?? "—"}
            </Typography>
          </Box>
        )}
      </Paper>

      {error && (
        <Alert severity="warning" sx={{ mb: 3, borderRadius: "10px" }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 10 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Grid container spacing={3}>

          {/* Attendance summary */}
          <Grid item xs={12} md={5}>
            <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DS.border}` }}>
                <SectionHeader title="Attendance" />
                <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                  <Chip label={`${presentCount} Present`} size="small" sx={{ bgcolor: "var(--color-success-bg)", color: "#15803d", fontWeight: 700 }} />
                  <Chip label={`${tardyCount} Tardy`} size="small" sx={{ bgcolor: "var(--color-warn-bg)", color: "var(--color-warn)", fontWeight: 700 }} />
                  <Chip label={`${noShowCount} No-Show`} size="small" sx={{ bgcolor: "var(--color-danger-bg)", color: "var(--color-danger)", fontWeight: 700 }} />
                </Box>
                {attendanceRate !== null && (
                  <Box sx={{ mt: 1.5 }}>
                    <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={600}>Attendance Rate</Typography>
                      <Typography variant="caption" color={tPrimary} fontWeight={700}>{attendanceRate}%</Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      value={attendanceRate}
                      sx={{
                        height: 8, borderRadius: 4,
                        bgcolor: "#e8f0f8",
                        "& .MuiLinearProgress-bar": { bgcolor: tPrimary, borderRadius: 4 },
                      }}
                    />
                  </Box>
                )}
              </Box>

              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: DS.surface }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>SESSION</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>STATUS</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: "0.75rem", color: "text.secondary" }}>DATE</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {attendance.slice(0, 8).map((rec, i) => (
                    <TableRow key={i} hover>
                      <TableCell>
                        <Typography variant="caption" fontWeight={600}>Session {rec.sessionNumber || i + 1}</Typography>
                      </TableCell>
                      <TableCell><StatusChip status={rec.status} /></TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {rec.date ? new Date(rec.date).toLocaleDateString("en-US", { month: "short", day: "numeric" }) : "—"}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                  {attendance.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={3} align="center" sx={{ py: 3 }}>
                        <Typography variant="caption" color="text.secondary">No attendance records yet.</Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </Paper>
          </Grid>

          {/* Right column: Checklists + Scenarios */}
          <Grid item xs={12} md={7}>

            {/* Skill checklists */}
            <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, overflow: "hidden", mb: 3 }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DS.border}` }}>
                <SectionHeader title="Skill Checklists" />
              </Box>
              {checklistStats.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">No checklists for this course.</Typography>
                </Box>
              ) : (
                checklistStats.map((cl, i) => (
                  <Box key={cl.id}>
                    {i > 0 && <Divider />}
                    <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{cl.title}</Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5, flexWrap: "wrap" }}>
                          <Chip
                            icon={cl.peerDone ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label="Peer"
                            size="small"
                            sx={{
                              bgcolor: cl.peerDone ? "var(--color-success-bg)" : "var(--color-surface-2)",
                              color: cl.peerDone ? "#15803d" : "var(--color-ink-2)",
                              fontWeight: 600, fontSize: "0.68rem",
                              "& .MuiChip-icon": { fontSize: 14 },
                            }}
                          />
                          <Chip
                            icon={cl.instructorDone ? <CheckCircleIcon /> : <RadioButtonUncheckedIcon />}
                            label="Instructor"
                            size="small"
                            sx={{
                              bgcolor: cl.instructorDone ? "var(--color-success-bg)" : "var(--color-surface-2)",
                              color: cl.instructorDone ? "#15803d" : "var(--color-ink-2)",
                              fontWeight: 600, fontSize: "0.68rem",
                              "& .MuiChip-icon": { fontSize: 14 },
                            }}
                          />
                          {cl.grade !== null && (
                            <Chip
                              label={`${cl.grade}%`}
                              size="small"
                              sx={{
                                bgcolor: cl.grade >= 70 ? "var(--color-success-bg)" : "var(--color-danger-bg)",
                                color: cl.grade >= 70 ? "#15803d" : "var(--color-danger)",
                                fontWeight: 700, fontSize: "0.68rem",
                              }}
                            />
                          )}
                        </Box>
                      </Box>
                      {cl.peerDone && !cl.instructorDone && (
                        <Button
                          size="small"
                          variant="outlined"
                          endIcon={<ArrowForwardIcon />}
                          onClick={() =>
                            navigate(
                              `/instructor/courses/${courseId}/checklists/${cl.id}/signoff/${studentId}`
                            )
                          }
                          sx={{
                            borderRadius: "6px", textTransform: "none",
                            fontWeight: 700, fontSize: "0.75rem",
                            borderColor: tPrimary, color: tPrimary,
                            ml: 1, flexShrink: 0,
                          }}
                        >
                          Sign Off
                        </Button>
                      )}
                    </Box>
                  </Box>
                ))
              )}
            </Paper>

            {/* Scenario tests */}
            <Paper elevation={0} sx={{ borderRadius: "12px", border: `1px solid ${DS.border}`, overflow: "hidden" }}>
              <Box sx={{ px: 2.5, py: 2, borderBottom: `1px solid ${DS.border}` }}>
                <SectionHeader title="Scenario Tests" />
              </Box>
              {scenarios.length === 0 ? (
                <Box sx={{ py: 4, textAlign: "center" }}>
                  <Typography variant="caption" color="text.secondary">No scenario records yet.</Typography>
                </Box>
              ) : (
                scenarios.map((sc, i) => (
                  <Box key={sc.id || i}>
                    {i > 0 && <Divider />}
                    <Box sx={{ px: 2.5, py: 1.5, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="body2" fontWeight={700} noWrap>{sc.title}</Typography>
                        <Box sx={{ display: "flex", gap: 1, mt: 0.5 }}>
                          {sc.topic && (
                            <Chip label={sc.topic} size="small" sx={{ bgcolor: "#e8f0f8", color: tPrimary, fontWeight: 600, fontSize: "0.68rem" }} />
                          )}
                          {sc.type && (
                            <Chip label={sc.type} size="small" sx={{ bgcolor: "var(--color-surface-2)", color: "var(--color-ink-2)", fontWeight: 600, fontSize: "0.68rem" }} />
                          )}
                        </Box>
                      </Box>
                      <Box sx={{ ml: 2, textAlign: "right", flexShrink: 0 }}>
                        {sc.peerComplete ? (
                          <CheckCircleIcon sx={{ color: "var(--color-success)", fontSize: 22 }} />
                        ) : (
                          <CancelIcon sx={{ color: "#d1d5db", fontSize: 22 }} />
                        )}
                        <Typography variant="caption" display="block" color="text.secondary">
                          {sc.peerComplete ? "Peer done" : "Pending"}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                ))
              )}
            </Paper>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}
